import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import useLatest from 'react-use/lib/useLatest'
import { elementTypes, TransmitterMesh, useModelClickListenerV2 } from '@scene'
import { AxiosError } from 'axios'
import {
  filter,
  find,
  findIndex,
  isNull,
  isUndefined,
  map,
  reject,
  remove,
  sortBy,
  toNumber,
} from 'lodash-es'
import { LoadingButton } from '@mui/lab'
import { Checkbox, FormControlLabel, Typography } from '@mui/material'
import { Stack } from '@mui/system'
import { useCameraStore } from '@modugen/ui-toolkit'
import { ThreeEvent } from '@react-three/fiber'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AddButton, FocusIconButton, SaveButton } from '@ui/actions'
import { ErrorField, Form, TextField } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { Box, CollapsibleDivider } from '@ui/structure'
import {
  useControlStore,
  useElementSelectionStore,
  useModelStore,
  useTransmissionStore,
} from '@editorStores'
import {
  useBlockScene,
  useFloorplanClickListener,
  useFloorplanElementTypeToSelectable,
  useGuidToElement,
  useHiddenElements,
  useModelClickListeners,
  useResultsInvalidation,
  useSelectionMode,
  useTypeInteraction,
  useVerticalTransmitters,
} from '@editorHooks'
import { getStiffeningIntervals } from '@queries'
import {
  recalculateVerticalTargets,
  saveVerticalTransmissionGraph,
  saveVerticalTransmissionTarget,
} from '@mutations'
import useElementTypes from 'src/components/pages/Editor/hooks/useElementTypes'
import useSceneElement from 'src/components/pages/Editor/hooks/useSceneElement'
import { getPointAtPercentage } from 'src/components/scene/utils'
import { buildErrorMessage } from 'src/constants'
import { signedDistanceInDirectionOf } from 'src/geometry/distance'
import { ConnectionDetailsEdit } from './connectionDetailsEdit'
import { ADD_VERTICAL_TRANSMISSION_TARGET_MODE_KEY, notAllowedTargets } from './constants'
import { verticalTransmitterSchema } from './schema'
import { TransmitterSupportWithTargets } from './transmitterSupport'

interface Props {
  selectedElement: string
  verticalTransmitter?: string | null
  setVerticalTransmitter?: (guid: string, elementGuid: string) => void
  disableChanging?: boolean
}

interface VerticalTransmitterFormValues {
  verticalTransmissionGraph: VerticalTransmissionGraph
  selectedSupportGuid: string
  selectedTargetGuid: string
  connectionSearchSettings: ConnectionSearchSettings
}

interface VerticalTransmitterListFieldsProps extends Props {
  isSavingVerticalGraph: boolean
  selectedElementType?: ElementTypes
  onAdd: (graph: VerticalTransmissionGraph) => void
}

interface LocalVerticalTransmittersPreviewProps {
  verticalTransmissionGraph: VerticalTransmissionGraph
  selectedElement: string
  transmitterGuid?: string | null
}

const makeDefaultConnectionSearchSettings = (): ConnectionSearchSettings => ({
  beam: {
    maxDistanceSnapToEndsMainElement: 0.05,
    maxDistanceSnapToEndsTargetElement: 0.05,
    mainElementExtension: 0.05,
    targetElementExtension: 0.01,
    beam_column_orthogonal_offset: 0.2,
    beam_column_parallel_offset: 0.2,
    beam_line_orthogonal_offset: 0.2,
    beam_line_min_interval_length: 0.1,
    beam_line_collinear_tolerance: 0.001,
  },
  column: {
    colum_column_max_offset: 0.2,
    column_line_parallel_offset: 0.2,
    column_line_orthogonal_offset: 0.2,
  },
  slab: {
    base_domain_orthogonal_offset: 0.1,
    base_domain_collinear_tolerance: 0.0001,
    allowed_purlin_z_offset: 0.15,
    min_interval_length: 0.1,
    use_ortho_domain_outplane_distance_threshold: false,
    ortho_domain_max_outplane_distance: 0.15,
    vertical_roof_slab_beam_z_offset: 0.2,
    vertical_roof_slab_purlin_z_offset: 0.2,
  },
  wall: {
    base_domain_max_orthogonal_offset: 0.1,
    base_domain_collinear_tolerance: 0.0001,
    min_interval_length: 0.1,
  },
})

const removeVerticalTransmitterTarget = (
  graph: VerticalTransmissionGraph,
  targetGuid: string,
): VerticalTransmissionGraph => {
  const supportTargets = [...graph.support_targets]
  const [supportTarget] = remove(supportTargets, [
    'target_guid',
    targetGuid,
  ]) as ElementSupportTarget[]
  const shouldKeepSupport =
    supportTarget &&
    supportTargets.some(({ support_guid }) => support_guid === supportTarget.support_guid)

  return {
    ...graph,
    element_targets: reject(graph.element_targets, ['guid', targetGuid]),
    support_targets: supportTargets,
    element_supports:
      shouldKeepSupport || !supportTarget
        ? graph.element_supports
        : reject(graph.element_supports, ['guid', supportTarget.support_guid]),
  }
}

const getSelectedElementVerticalTransmissionGraph = (
  graph: VerticalTransmissionGraph,
  selectedElement: string,
): VerticalTransmissionGraph => {
  const element_supports = filter(graph.element_supports, ['element_guid', selectedElement])
  const supportGuids = map(element_supports, 'guid')
  const support_targets = filter(graph.support_targets, ({ support_guid }) =>
    supportGuids.includes(support_guid),
  )
  const targetGuids = map(support_targets, 'target_guid')
  const element_targets = filter(graph.element_targets, ({ guid }) => targetGuids.includes(guid))

  return {
    ...graph,
    element_supports,
    support_targets,
    element_targets,
  }
}

const LocalVerticalTransmittersPreview = ({
  verticalTransmissionGraph,
  selectedElement,
  transmitterGuid,
}: LocalVerticalTransmittersPreviewProps): ReactElement | null => {
  const domains = useModelStore(state => state.domains)
  const hiddenElements = useHiddenElements()
  const isOrthographic = useCameraStore(state => state.isOrthographic)

  const selectedElementGraph = useMemo(
    () => getSelectedElementVerticalTransmissionGraph(verticalTransmissionGraph, selectedElement),
    [selectedElement, verticalTransmissionGraph],
  )

  const hiddenWithoutSelectedElement = useMemo<Set<string>>(() => {
    return new Set(filter(hiddenElements, element => element !== selectedElement) as string[])
  }, [hiddenElements, selectedElement])

  if (isOrthographic || selectedElementGraph.element_supports.length === 0) return null

  return (
    <TransmitterMesh
      data={selectedElementGraph}
      pointConnectorKind="vertical"
      domains={domains}
      transmitterGuid={transmitterGuid || undefined}
      disableClick
      hiddenElements={hiddenWithoutSelectedElement}
    />
  )
}

const VerticalTransmitterListFields = ({
  selectedElement,
  verticalTransmitter,
  setVerticalTransmitter,
  disableChanging = false,
  isSavingVerticalGraph,
  selectedElementType,
  onAdd,
}: VerticalTransmitterListFieldsProps): ReactElement => {
  const { getValues, setValue } = useFormContext<VerticalTransmitterFormValues>()
  const { setSelectionMode, unsetSelectionMode, selectionModeKey } = useSelectionMode()
  const actionMode = useControlStore(state => state.actionMode)
  const { enqueueSnackbar } = useSnackbar()
  const { projectId }: { projectId?: string } = useParams()
  const invalidateResults = useResultsInvalidation()
  const setSecondaryHighlightedIds = useElementSelectionStore(
    state => state.setSecondaryHighlightedIds,
  )

  const focusElements = useModelStore(state => state.focusElements)

  const localVerticalTransmissionGraph = useWatch({
    name: 'verticalTransmissionGraph',
  })
  const addTargetSupportGuidRef = useRef<string>()

  const previewMeshProps = useMemo(
    () => ({
      verticalTransmissionGraph: localVerticalTransmissionGraph,
      selectedElement,
      transmitterGuid: verticalTransmitter,
    }),
    [localVerticalTransmissionGraph, selectedElement, verticalTransmitter],
  )

  const selectedSupportGuid = useWatch({ name: 'selectedSupportGuid' })
  const selectedTargetGuid = useWatch({ name: 'selectedTargetGuid' })
  const useOrthoDomainOutplaneDistanceThreshold = useWatch({
    name: 'connectionSearchSettings.slab.use_ortho_domain_outplane_distance_threshold',
  })

  const verticalTransmitters = useVerticalTransmitters(
    localVerticalTransmissionGraph,
    selectedElement,
  )

  useSceneElement({
    enabled: true,
    properties: previewMeshProps,
    component: LocalVerticalTransmittersPreview,
  })

  const { mutate: recalculateVertical, isLoading: isRecalculatingVertical } = useMutation(
    () => recalculateVerticalTargets.request(projectId, selectedElement),
    {
      onSuccess: async data => {
        onAdd(data)
        setValue('verticalTransmissionGraph', data)
        invalidateResults(projectId as string)
        enqueueSnackbar('Ziele erfolgreich neu berechnet', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim der Berechnung der Ziele'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutate: addVerticalTransmissionTarget, isLoading: isSavingVerticalTransmissionTarget } =
    useMutation(
      (data: {
        hostElementId: string
        targetElementId: string
        graph: VerticalTransmissionGraph
        supportGuid?: string
        connectionSearchSettings: ConnectionSearchSettings
      }) =>
        saveVerticalTransmissionTarget.request(
          projectId,
          data.hostElementId,
          data.targetElementId,
          data.graph,
          data.connectionSearchSettings,
          data.supportGuid,
        ),
      {
        onSuccess: async data => {
          onAdd(data)
          setValue('verticalTransmissionGraph', data)
          invalidateResults(projectId as string)
          enqueueSnackbar('Ziele erfolgreich gespeichert', { variant: 'success' })
        },
        onError: (error: AxiosError) => {
          enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Hinzufügen des Ziels'), {
            variant: 'error',
          })
        },
      },
    )

  useBlockScene(isSavingVerticalTransmissionTarget)

  const isRecalculated = useMemo(
    () => !find(verticalTransmitters, { guid: verticalTransmitter }),
    [verticalTransmitter, verticalTransmitters],
  )

  useEffect(() => {
    if (isNull(verticalTransmitters)) return

    const activeTransmitter = find(verticalTransmitters, ({ guid }) => guid === verticalTransmitter)

    if ((isRecalculated || !activeTransmitter) && verticalTransmitters.length) {
      const { guid, element_guid } = verticalTransmitters[0]
      setVerticalTransmitter?.(guid, element_guid)
      // Keep the list's row highlight (form state) in sync with the auto-selected transmitter,
      // just like clicking a support does — otherwise only the scene reflects the default.
      setValue('selectedSupportGuid', guid)
    }
  }, [isRecalculated, setValue, setVerticalTransmitter, verticalTransmitter, verticalTransmitters])

  useEffect(() => {
    if (isNull(verticalTransmitters)) return

    const targetGuids = verticalTransmitters.reduce((acc, item: ElementSupportItemWithTargets) => {
      return [...acc, ...item.targets.map(({ element_guid }) => element_guid)]
    }, [] as string[])

    setSecondaryHighlightedIds(targetGuids)

    return () => setSecondaryHighlightedIds([])
  }, [setSecondaryHighlightedIds, verticalTransmitters])

  const handleFocusClick = () => {
    if (isNull(verticalTransmitters)) return

    const targetGuids = verticalTransmitters.reduce((acc, item: ElementSupportItemWithTargets) => {
      return [...acc, ...item.targets.map(({ element_guid }) => element_guid)]
    }, [] as string[])

    focusElements([selectedElement, ...targetGuids])
  }

  const interactableTypes = useMemo(() => {
    if (isUndefined(selectedElementType)) return 'none'
    const unallowed = notAllowedTargets[selectedElementType]
    return reject(Object.keys(notAllowedTargets) as ElementTypes[], type =>
      unallowed.includes(type),
    )
  }, [selectedElementType])

  useTypeInteraction(
    selectionModeKey === ADD_VERTICAL_TRANSMISSION_TARGET_MODE_KEY ? interactableTypes : 'all',
  )

  const floorplanElementTypeToSelectable = useMemo(() => {
    if (selectionModeKey !== ADD_VERTICAL_TRANSMISSION_TARGET_MODE_KEY) return null

    if (interactableTypes === 'none') {
      return elementTypes.reduce((acc, type) => {
        acc[type] = false
        return acc
      }, {} as Partial<Record<ElementTypes, boolean>>)
    }

    const allowed = new Set(interactableTypes)
    return elementTypes.reduce((acc, type) => {
      if (!allowed.has(type)) {
        acc[type] = false
      }
      return acc
    }, {} as Partial<Record<ElementTypes, boolean>>)
  }, [interactableTypes, selectionModeKey])

  useFloorplanElementTypeToSelectable(floorplanElementTypeToSelectable)

  const handleStartAddTargetSelection = useCallback(
    (supportGuid?: string) => {
      addTargetSupportGuidRef.current = supportGuid
      setSelectionMode({
        message: 'Klicken sie ein Element an, das als Lastziel verwendet wird',
        key: ADD_VERTICAL_TRANSMISSION_TARGET_MODE_KEY,
      })
    },
    [setSelectionMode],
  )

  const onClickTargetLatest = useLatest((elementGuid: string) => {
    const supportGuid = addTargetSupportGuidRef.current
    const connectionSearchSettings = getValues('connectionSearchSettings')
    const parsedConnectionSearchSettings: ConnectionSearchSettings = {
      ...connectionSearchSettings,
      beam: {
        ...connectionSearchSettings.beam,
        maxDistanceSnapToEndsMainElement: toNumber(
          connectionSearchSettings.beam.maxDistanceSnapToEndsMainElement,
        ),
        maxDistanceSnapToEndsTargetElement: toNumber(
          connectionSearchSettings.beam.maxDistanceSnapToEndsTargetElement,
        ),
        mainElementExtension: toNumber(connectionSearchSettings.beam.mainElementExtension),
        targetElementExtension: toNumber(connectionSearchSettings.beam.targetElementExtension),
        beam_column_orthogonal_offset: toNumber(
          connectionSearchSettings.beam.beam_column_orthogonal_offset,
        ),
        beam_column_parallel_offset: toNumber(
          connectionSearchSettings.beam.beam_column_parallel_offset,
        ),
        beam_line_orthogonal_offset: toNumber(
          connectionSearchSettings.beam.beam_line_orthogonal_offset,
        ),
        beam_line_min_interval_length: toNumber(
          connectionSearchSettings.beam.beam_line_min_interval_length,
        ),
        beam_line_collinear_tolerance: toNumber(
          connectionSearchSettings.beam.beam_line_collinear_tolerance,
        ),
      },
      column: {
        ...connectionSearchSettings.column,
        colum_column_max_offset: toNumber(connectionSearchSettings.column.colum_column_max_offset),
        column_line_parallel_offset: toNumber(
          connectionSearchSettings.column.column_line_parallel_offset,
        ),
        column_line_orthogonal_offset: toNumber(
          connectionSearchSettings.column.column_line_orthogonal_offset,
        ),
      },
      slab: {
        ...connectionSearchSettings.slab,
        base_domain_orthogonal_offset: toNumber(
          connectionSearchSettings.slab.base_domain_orthogonal_offset,
        ),
        base_domain_collinear_tolerance: toNumber(
          connectionSearchSettings.slab.base_domain_collinear_tolerance,
        ),
        allowed_purlin_z_offset: toNumber(connectionSearchSettings.slab.allowed_purlin_z_offset),
        min_interval_length: toNumber(connectionSearchSettings.slab.min_interval_length),
        ortho_domain_max_outplane_distance: toNumber(
          connectionSearchSettings.slab.ortho_domain_max_outplane_distance,
        ),
        vertical_roof_slab_beam_z_offset: toNumber(
          connectionSearchSettings.slab.vertical_roof_slab_beam_z_offset,
        ),
        vertical_roof_slab_purlin_z_offset: toNumber(
          connectionSearchSettings.slab.vertical_roof_slab_purlin_z_offset,
        ),
      },
      wall: {
        ...connectionSearchSettings.wall,
        base_domain_max_orthogonal_offset: toNumber(
          connectionSearchSettings.wall.base_domain_max_orthogonal_offset,
        ),
        base_domain_collinear_tolerance: toNumber(
          connectionSearchSettings.wall.base_domain_collinear_tolerance,
        ),
        min_interval_length: toNumber(connectionSearchSettings.wall.min_interval_length),
      },
    }

    addVerticalTransmissionTarget({
      hostElementId: selectedElement,
      targetElementId: elementGuid,
      graph: localVerticalTransmissionGraph,
      supportGuid,
      connectionSearchSettings: parsedConnectionSearchSettings,
    })

    addTargetSupportGuidRef.current = undefined
    unsetSelectionMode()
  })

  const onClickTargetEvent = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      const elementGuid = event.object.name
      onClickTargetLatest.current(elementGuid)
    },
    [onClickTargetLatest],
  )

  useModelClickListeners(
    onClickTargetEvent,
    [selectedElement, localVerticalTransmissionGraph, selectedElementType],
    selectionModeKey === ADD_VERTICAL_TRANSMISSION_TARGET_MODE_KEY,
    true,
  )

  const onClickTargetClb = useCallback(
    (elementGuid: string) => {
      onClickTargetLatest.current(elementGuid)
    },
    [onClickTargetLatest],
  )

  useModelClickListenerV2('position', onClickTargetClb, {
    enabled:
      selectionModeKey === ADD_VERTICAL_TRANSMISSION_TARGET_MODE_KEY && actionMode !== 'hide',
    guids: null,
  })

  useFloorplanClickListener(onClickTargetClb, {
    enabled:
      selectionModeKey === ADD_VERTICAL_TRANSMISSION_TARGET_MODE_KEY && actionMode !== 'hide',
  })

  const guidToElement = useGuidToElement()
  const element = useMemo(() => guidToElement[selectedElement], [guidToElement, selectedElement])
  const domains = useModelStore(state => state.domains)
  const elementDomains = useMemo(
    () => filter(domains, ['element_guid', selectedElement]),
    [domains, selectedElement],
  )

  const elementStart = useMemo(
    () => element && ('start' in element.shape ? element.shape.start : element.shape.points[0]),
    [element],
  )
  const elementEnd = useMemo(
    () => element && ('end' in element.shape ? element.shape.end : element.shape.points[1]),
    [element],
  )
  const elementDirection = useMemo(
    () =>
      element &&
      ('start' in element.shape
        ? element.shape.start.directionTo(element.shape.end)
        : element.shape.points[1].directionTo(element.shape.points[2])),
    [element],
  )
  const elementLength = elementStart && elementEnd && elementStart.distanceTo(elementEnd)

  const sorted = useMemo(
    () =>
      sortBy(verticalTransmitters || [], el => {
        const domain = el.domain || find(elementDomains, { guid: el.domain_guid })
        const relative_position = el.relative_position || el.relative_interval?.lower
        const pointForSorting =
          relative_position &&
          domain &&
          getPointAtPercentage(domain.start, domain.end, toNumber(relative_position))

        return (
          pointForSorting &&
          signedDistanceInDirectionOf(elementStart, pointForSorting, elementDirection)
        )
      }),
    [elementDirection, elementDomains, elementStart, verticalTransmitters],
  )

  const selectedTargetIndex = useMemo(
    () => findIndex(localVerticalTransmissionGraph.element_targets, { guid: selectedTargetGuid }),
    [localVerticalTransmissionGraph.element_targets, selectedTargetGuid],
  )

  const handleClickSupport = useCallback(
    (supportGuid: string) => {
      setValue('selectedSupportGuid', supportGuid)
      setVerticalTransmitter?.(supportGuid, selectedElement)
    },
    [setValue, setVerticalTransmitter, selectedElement],
  )

  const handleDeleteTarget = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, guid: string) => {
      event.stopPropagation()

      const transmissionGraph = getValues('verticalTransmissionGraph')

      const newTransmissionGraph = removeVerticalTransmitterTarget(transmissionGraph, guid)

      if (guid === selectedSupportGuid) {
        const firstSupport = find(newTransmissionGraph.element_supports, {
          element_guid: selectedElement,
        })

        setValue('selectedSupportGuid', firstSupport ? firstSupport.guid : '')
      }
      setValue('verticalTransmissionGraph', newTransmissionGraph)

      enqueueSnackbar('Verbindung entfernt. Änderungen müssen noch gespeichert werden', {
        variant: 'info',
      })
    },
    [enqueueSnackbar, getValues, selectedElement, selectedSupportGuid, setValue],
  )

  const isBeamLikeSelected = selectedElementType === 'beams' || selectedElementType === 'purlins'
  const isSlabLikeSelected =
    selectedElementType === 'vertical_slabs' || selectedElementType === 'vertical_roof_slabs'
  const isColumnSelected = selectedElementType === 'columns' || selectedElementType === 'rips'
  const isWallSelected =
    selectedElementType === 'inner_walls' || selectedElementType === 'outer_walls'
  const showAdvancedSettings =
    isBeamLikeSelected || isSlabLikeSelected || isColumnSelected || isWallSelected

  return (
    <Stack spacing={1}>
      {sorted.length > 0 && (
        <Box data-cy="vertical-transmitter-list">
          {sorted.map((supportWithTargets: ElementSupportItemWithTargets, index: number) => {
            const { guid: supportGuid } = supportWithTargets

            const allowDelete = !disableChanging
            const allowAddTarget = !disableChanging

            return (
              <Stack direction={'row'} key={supportGuid} alignItems="center" spacing={1}>
                <Typography fontSize="small">{index + 1}</Typography>
                <TransmitterSupportWithTargets
                  supportWithTargets={supportWithTargets}
                  elementLength={elementLength}
                  selectedSupportGuid={selectedSupportGuid}
                  selectedTargetGuid={selectedTargetGuid}
                  handleClickSupport={() => handleClickSupport(supportGuid)}
                  handleDeleteTarget={handleDeleteTarget}
                  allowDelete={allowDelete}
                  handleAddTarget={() => {
                    handleClickSupport(supportGuid)
                    handleStartAddTargetSelection(supportGuid)
                  }}
                  allowAddTarget={allowAddTarget}
                />
              </Stack>
            )
          })}
        </Box>
      )}
      <ErrorField name="verticalTransmissionGraph" />
      {selectedTargetIndex !== -1 && (
        <ConnectionDetailsEdit
          // Force remount whenever the selected target's guid OR its position in
          // `element_targets` changes, so the inner `useController` never sees a
          // dynamic `name` change on a live instance. RHF unconditionally calls
          // `unregister(previousName)` on name change for non-field-array paths,
          // which would delete `transmission_factor` from whatever target now sits
          // at the old index (e.g. after a delete/add/recalculate reshuffles the
          // array) and cause a validation error on save.
          key={`${selectedTargetGuid}-${selectedTargetIndex}`}
          selectedTargetIndex={selectedTargetIndex}
          disableChanging={disableChanging}
        />
      )}
      {!disableChanging && (
        <Stack spacing={1}>
          {showAdvancedSettings && (
            <CollapsibleDivider
              text="Erweiterte Einstellungen für Auflagersuche"
              defaultExpanded={false}
            >
              <Stack spacing={1} mt={1}>
                {isBeamLikeSelected && (
                  <>
                    <TextField
                      inputLabel="Verlängerung Hauptelement (Linienartig)"
                      name="connectionSearchSettings.beam.mainElementExtension"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                    <TextField
                      inputLabel="Verlängerung Zielelement (Linienartig)"
                      name="connectionSearchSettings.beam.targetElementExtension"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                    <TextField
                      inputLabel="max. Abstand zu Enden des Hauptelements"
                      name="connectionSearchSettings.beam.maxDistanceSnapToEndsMainElement"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                    <TextField
                      inputLabel="max. Abstand zu Enden des Zielelements"
                      name="connectionSearchSettings.beam.maxDistanceSnapToEndsTargetElement"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                    <TextField
                      inputLabel="max. orthogonaler Abstand zu Stützen"
                      name="connectionSearchSettings.beam.beam_column_orthogonal_offset"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                    <TextField
                      inputLabel="max. paralleler Abstand zu Stützen"
                      name="connectionSearchSettings.beam.beam_column_parallel_offset"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                    <TextField
                      inputLabel="max. orthogonaler Abstand zu Linien"
                      name="connectionSearchSettings.beam.beam_line_orthogonal_offset"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                    <TextField
                      inputLabel="Mindestlänge von Linienauflagern"
                      name="connectionSearchSettings.beam.beam_line_min_interval_length"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                  </>
                )}

                {isColumnSelected && (
                  <>
                    <TextField
                      inputLabel="max. Abstand Stütze zu Stütze"
                      name="connectionSearchSettings.column.colum_column_max_offset"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                    <TextField
                      inputLabel="max. Abstand in Achsrichtung"
                      name="connectionSearchSettings.column.column_line_parallel_offset"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                    <TextField
                      inputLabel="max. Abstand orthogonal zur Achsrichtung"
                      name="connectionSearchSettings.column.column_line_orthogonal_offset"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                  </>
                )}

                {isWallSelected && (
                  <>
                    <TextField
                      inputLabel="max. Abstand quer zur Wandachse"
                      name="connectionSearchSettings.wall.base_domain_max_orthogonal_offset"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                    <TextField
                      inputLabel="Mindestlänge von Linienauflagern"
                      name="connectionSearchSettings.wall.min_interval_length"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                  </>
                )}

                {isSlabLikeSelected && (
                  <>
                    <TextField
                      inputLabel="max. orthogonaler Abstand zur Basislinie"
                      name="connectionSearchSettings.slab.base_domain_orthogonal_offset"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                    <TextField
                      inputLabel="max. Abstand in Z-Richtung zu Pfetten"
                      name="connectionSearchSettings.slab.allowed_purlin_z_offset"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                    <TextField
                      inputLabel="Mindestlänge von Linienauflagern"
                      name="connectionSearchSettings.slab.min_interval_length"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                    <TextField
                      inputLabel="max. Abstand in Z-Richtung zw. Unterzügen und Dachpositionen"
                      name="connectionSearchSettings.slab.vertical_roof_slab_beam_z_offset"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                    <TextField
                      inputLabel="max. Abstand in Z-Richtung zw. Pfetten und Dachpositionen"
                      name="connectionSearchSettings.slab.vertical_roof_slab_purlin_z_offset"
                      type="number"
                      size="small"
                      inputProps={{ step: 0.01, min: 0 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(useOrthoDomainOutplaneDistanceThreshold)}
                          onChange={e =>
                            setValue(
                              'connectionSearchSettings.slab.use_ortho_domain_outplane_distance_threshold',
                              e.target.checked,
                            )
                          }
                          size="small"
                        />
                      }
                      label="Outplane-Schwelle für orthogonale Linien verwenden"
                    />
                  </>
                )}
              </Stack>
            </CollapsibleDivider>
          )}

          <Stack display="flex" justifyContent="center" direction="row" spacing={1}>
            <AddButton
              size="small"
              fullWidth
              onClick={() => handleStartAddTargetSelection()}
              loading={isSavingVerticalTransmissionTarget}
              data-cy="add-vertical-transmitter-btn"
            >
              Hinzufügen
            </AddButton>
            <LoadingButton
              onClick={() => recalculateVertical()}
              color="primary"
              variant="contained"
              size="small"
              fullWidth
              loading={isRecalculatingVertical}
            >
              Neu berechnen
            </LoadingButton>
            <SaveButton
              type="submit"
              loading={isSavingVerticalGraph}
              size="small"
              fullWidth
              data-cy="btn-submit"
            >
              Speichern
            </SaveButton>
          </Stack>
        </Stack>
      )}
      {!isNull(verticalTransmitters) && verticalTransmitters.length > 0 && (
        <Stack display="flex" justifyContent="center" direction="row" spacing={1}>
          <FocusIconButton
            hint="Auf ausgewähltes Element und alle Ziele fokussieren"
            onClick={handleFocusClick}
          />
        </Stack>
      )}
    </Stack>
  )
}

const VerticalTransmitterList = ({
  selectedElement,
  verticalTransmitter,
  setVerticalTransmitter,
  disableChanging = false,
}: Props): ReactElement => {
  const { projectId }: { projectId?: string } = useParams()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const invalidateResults = useResultsInvalidation()
  const verticalTransmissionGraph = useTransmissionStore(state => state.verticalTransmissionGraph)
  const setVerticalTransmissionGraph = useTransmissionStore(
    state => state.setVerticalTransmissionGraph,
  )
  const elementsByType = useElementTypes()
  const selectedElementType = useMemo(
    () => elementsByType[selectedElement],
    [elementsByType, selectedElement],
  )

  const { mutateAsync: saveVerticalGraph, isLoading: isSavingVerticalGraph } = useMutation(
    (data: VerticalTransmissionGraph) => saveVerticalTransmissionGraph.request(projectId, data),
    {
      onSuccess: async data => {
        setVerticalTransmissionGraph(data)
        await queryClient.invalidateQueries(getStiffeningIntervals.getKey(projectId))
        invalidateResults(projectId as string)
        enqueueSnackbar('Kraftübertragung erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Kraftübertragung'), {
          variant: 'error',
        })
      },
    },
  )

  const defaultValues = useMemo(() => {
    const selectedSupportGuid = verticalTransmitter
    const selectedTargetGuid = verticalTransmissionGraph.support_targets.find(
      ({ support_guid }) => support_guid === verticalTransmitter,
    )?.target_guid

    return {
      verticalTransmissionGraph,
      selectedSupportGuid,
      selectedTargetGuid,
      connectionSearchSettings: makeDefaultConnectionSearchSettings(),
    }
  }, [verticalTransmissionGraph, verticalTransmitter])

  return (
    <Form
      key={selectedElement}
      defaultValues={defaultValues}
      validationSchema={verticalTransmitterSchema}
      validationContext={{
        selectedElement,
        selectedElementType,
      }}
      onSubmit={async ({ verticalTransmissionGraph: graph }: VerticalTransmitterFormValues) => {
        await saveVerticalGraph(graph)
      }}
    >
      <VerticalTransmitterListFields
        selectedElement={selectedElement}
        verticalTransmitter={verticalTransmitter}
        setVerticalTransmitter={setVerticalTransmitter}
        disableChanging={disableChanging}
        isSavingVerticalGraph={isSavingVerticalGraph}
        selectedElementType={selectedElementType}
        onAdd={setVerticalTransmissionGraph}
      />
    </Form>
  )
}

export default VerticalTransmitterList
